import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '../../api/endpoints';
import { currency } from '../../utils/format';
import { openDirections } from '../../utils/openMap';
import { fileUrl } from '../../api/client';
import { useCart } from '../../context/CartContext';
import {
  Avatar, Badge, Button, Chip, Empty, Input, Loading, Modal, Rating, SectionHeader,
} from '../../components/Ui';
import { ServiceCard, ProductCard } from '../../components/Cards';
import MenuItemCard from '../../components/MenuItemCard';
import { Reveal, stagger } from '../../components/Motion';
import PageHeader from '../../components/PageHeader';
import { colors } from '../../theme';
import { titleCase } from '../../utils/format';
import { vocabOf } from '../../utils/vertical';

/** Business profile: catalogue, services, menu, reviews and contact. */
const PartnerDetailPage = () => {
  // Opened from the partner panel with ?preview=1: same page, ordering switched off.
  const preview = new URLSearchParams(useLocation().search).get('preview') === '1';
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, quantityOf } = useCart();

  const [data, setData] = useState(null);
  const [tab, setTab] = useState('services');
  // Menu filters (restaurants): veg / price / section, chosen in a modal.
  const [menuFilter, setMenuFilter] = useState({ foodType: '', maxPrice: '', section: '' });
  const [draft, setDraft] = useState(menuFilter);
  const [menuFilterOpen, setMenuFilterOpen] = useState(false);
  const [shelfOpen, setShelfOpen] = useState(null);
  const [itemSearch, setItemSearch] = useState('');
  const [rackOpen, setRackOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  // Full-size viewer for a menu page or shop photo.
  const [viewer, setViewer] = useState(null);

  const load = useCallback(() => {
    api.partners.detail(id)
      .then((res) => {
        setData(res.data);
        setTab(res.data.services && res.data.services.length ? 'services' : 'products');
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loading text="Loading business…" />;
  if (!data) {
    return (
      <Empty emoji="🏪" title="Business not found" actionLabel="Go back" onAction={() => navigate(-1)} />
    );
  }

  const {
    partner, reviews, isRestaurant, menuCategories = [], minOrderAmount = 0,
  } = data;
  // A restaurant's products are its menu: filtered, then grouped by section.
  /*
   * The shop's own sections plus whatever its categories ask about - size,
   * colour, who it is for - filter the shelf the same way for every trade.
   */
  const attributeFilters = data.attributeFilters || [];
  const needle = itemSearch.trim().toLowerCase();
  const matches = (item) => {
    // Searching the shop's own shelf: the dish's name, what it is, its section.
    if (needle) {
      const hay = [item.title, item.description, item.menuCategory, item.brand,
        ...((item.attributes || []).flatMap((a) => a.values || []))].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    if (menuFilter.maxPrice && item.price > Number(menuFilter.maxPrice)) return false;
    if (menuFilter.section && (item.menuCategory || '') !== menuFilter.section) return false;
    return attributeFilters.every((f) => {
      const want = menuFilter[`attr_${f.key}`];
      if (!want) return true;
      return (item.attributes || []).some((a) => a.key === f.key && (a.values || []).includes(want));
    });
  };
  const services = (data.services || []).filter(matches);
  const products = (data.products || []).filter((pr) => {
    if (isRestaurant && menuFilter.foodType && pr.foodType !== menuFilter.foodType) return false;
    return matches(pr);
  });
  const menuSections = isRestaurant ? [...new Set(products.map((pr) => pr.menuCategory || 'Other'))] : [];
  /*
   * A shop that has built its own shelves is browsed like a store: the
   * categories run down the left, the goods sit two to a row on the right.
   */
  const shelves = data.shelves || [];
  const storeMode = tab === 'products' && shelves.length > 0;
  // A garment shop is walked differently: Men / Women / Kids across the top.
  const fashionMode = storeMode && data.storeLayout === 'fashion';
  const openShelf = shelves.find((sh) => String(sh._id) === String(shelfOpen)) || shelves[0];
  const racks = openShelf ? (openShelf.children || []) : [];
  const openRack = racks.find((r) => String(r._id) === String(rackOpen)) || racks[0];
  const shelfProducts = openShelf
    ? products.filter((p) => [openShelf._id, ...(openShelf.children || []).map((c) => c._id)].map(String).includes(String(p.section)))
    : products;
  // In a garment shop the rack decides what is on show; the first is open by default.
  const rackProducts = openRack ? products.filter((p) => String(p.section) === String(openRack._id)) : shelfProducts;

  const activeMenuFilters = ['foodType', 'maxPrice', 'section', ...attributeFilters.map((f) => `attr_${f.key}`)]
    .filter((k) => menuFilter[k]).length;
  const sectionOptions = tab === 'services'
    ? ((data.sections || {}).services || [])
    : (isRestaurant ? menuCategories : ((data.sections || {}).products || []));
  const filterDefaults = {
    foodType: '', maxPrice: '', section: '', ...Object.fromEntries(attributeFilters.map((f) => [`attr_${f.key}`, ''])),
  };
  const canFilter = isRestaurant || attributeFilters.length > 0 || sectionOptions.length > 0;
  const filterBar = (
    <>
      <div className="row" style={{ gap: 10, alignItems: 'center', marginBottom: 8 }}>
        <Input
          icon="🔍"
          className="grow"
          placeholder={tab === 'services'
            ? 'Search services'
            : isRestaurant ? 'Search dishes on this menu' : 'Search products in this shop'}
          value={itemSearch}
          onChange={(e) => setItemSearch(e.target.value)}
          style={{ marginBottom: 0 }}
        />
        {canFilter ? (
          <button
            type="button"
            className={`filter-btn ${activeMenuFilters ? 'on' : ''}`}
            onClick={() => { setDraft(menuFilter); setMenuFilterOpen(true); }}
            aria-label="Filter"
          >
            ⚙︎
            {activeMenuFilters ? <span className="filter-count">{activeMenuFilters}</span> : null}
          </button>
        ) : null}
      </div>
      <div className="t-tiny" style={{ marginBottom: 12 }}>
        {(tab === 'services' ? services : products).length}
        {' '}
        {tab === 'services' ? 'service' : (isRestaurant ? 'item' : 'product')}
        {(tab === 'services' ? services : products).length === 1 ? '' : 's'}
        {needle ? ` matching "${itemSearch.trim()}"` : ''}
        {menuFilter.section ? ` in ${menuFilter.section}` : ''}
      </div>
    </>
  );
  const filterModal = (
    <Modal open={menuFilterOpen} onClose={() => setMenuFilterOpen(false)} title={isRestaurant ? 'Filter the menu' : 'Filter'}>
      {isRestaurant ? (
        <>
          <label className="field-label">Type</label>
          <div className="row wrap" style={{ gap: 8, marginBottom: 14 }}>
            {[['', 'Everything'], ['veg', '🟢 Veg'], ['non_veg', '🔴 Non-veg'], ['egg', '🟠 Egg']].map(([v, l]) => (
              <Chip key={v || 'all'} label={l} active={draft.foodType === v} onClick={() => setDraft({ ...draft, foodType: v })} />
            ))}
          </div>
        </>
      ) : null}
      <label className="field-label">Price</label>
      <div className="row wrap" style={{ gap: 8, marginBottom: 14 }}>
        {[['', 'Any price'], ['100', 'Under ₹100'], ['200', 'Under ₹200'], ['350', 'Under ₹350'], ['600', 'Under ₹600']].map(([v, l]) => (
          <Chip key={v || 'any'} label={l} active={draft.maxPrice === v} onClick={() => setDraft({ ...draft, maxPrice: v })} />
        ))}
      </div>
      {sectionOptions.length ? (
        <>
          <label className="field-label">{isRestaurant ? 'Menu section' : 'Section'}</label>
          <div className="row wrap" style={{ gap: 8, marginBottom: 14 }}>
            <Chip label="All sections" active={!draft.section} onClick={() => setDraft({ ...draft, section: '' })} />
            {sectionOptions.map((c) => (
              <Chip key={c} label={c} active={draft.section === c} onClick={() => setDraft({ ...draft, section: c })} />
            ))}
          </div>
        </>
      ) : null}
      {/* Whatever this shop's categories ask about: size, colour, who it is for. */}
      {attributeFilters.map((f) => (
        <div key={f.key}>
          <label className="field-label">{f.label}</label>
          <div className="row wrap" style={{ gap: 8, marginBottom: 14 }}>
            <Chip label={`Any ${f.label.toLowerCase()}`} active={!draft[`attr_${f.key}`]} onClick={() => setDraft({ ...draft, [`attr_${f.key}`]: '' })} />
            {f.values.map((v) => (
              <Chip key={v} label={v} active={draft[`attr_${f.key}`] === v} onClick={() => setDraft({ ...draft, [`attr_${f.key}`]: v })} />
            ))}
          </div>
        </div>
      ))}
      <div className="row" style={{ gap: 10 }}>
        <Button title="Apply" onClick={() => { setMenuFilter(draft); setMenuFilterOpen(false); }} />
        <Button title="Reset" variant="light" onClick={() => setDraft(filterDefaults)} />
      </div>
    </Modal>
  );
  const menu = (partner.menu || []).filter((m) => m.image);
  const gallery = partner.gallery || [];

  // Tabs a business hasn't filled in are hidden rather than shown empty.
  const vocab = vocabOf(partner);
  const TABS = [
    services.length ? { key: 'services', label: `${vocab.key === 'travel' ? 'Packages & services' : 'Services'} (${services.length})` } : null,
    data.hotel ? { key: 'rooms', label: 'Rooms' } : null,
    (data.products || []).length
      ? { key: 'products', label: isRestaurant ? `Menu (${data.products.length})` : `Products (${products.length})` }
      : null,
    menu.length ? { key: 'menu', label: isRestaurant ? 'Menu card' : 'Menu' } : null,
    { key: 'about', label: 'About' },
    { key: 'reviews', label: `Reviews (${reviews.length})` },
  ].filter(Boolean);

  const whatsappNumber = partner.whatsapp || partner.phone;

  return (
    <div>
      {preview ? (
        <div className="row-between" style={{ background: 'var(--warning-bg)', borderRadius: 12, padding: '10px 14px', marginBottom: 14, gap: 12 }}>
          <div>
            <strong style={{ color: 'var(--warning)' }}>👀 Customer preview</strong>
            <div className="t-tiny">This is your store exactly as customers see it.</div>
          </div>
          <button type="button" className="link-btn" onClick={() => window.history.back()}>Back to partner panel</button>
        </div>
      ) : null}
      <PageHeader title={partner.businessName} subtitle={titleCase(partner.partnerType)} />

      {/* Cover */}
      <div style={{ borderRadius: 18, overflow: 'hidden', marginBottom: -34 }}>
        {partner.coverImage || partner.logo ? (
          <img
            src={fileUrl(partner.coverImage || partner.logo)}
            alt=""
            style={{ width: '100%', height: 220, objectFit: 'cover' }}
          />
        ) : (
          <div
            style={{
              height: 220, background: colors.primaryLight,
              display: 'grid', placeItems: 'center', fontSize: 54,
            }}
          >
            {(partner.primaryCategory && partner.primaryCategory.icon) || '🏪'}
          </div>
        )}
      </div>

      {/* Summary */}
      <Reveal className="card sh-md" style={{ position: 'relative', margin: '0 16px', padding: 18 }}>
        <div className="row" style={{ gap: 14 }}>
          <Avatar name={partner.businessName} uri={fileUrl(partner.logo)} size={58} />
          <div className="grow">
            <h3 className="h3">{partner.businessName}</h3>
            <div className="t-small" style={{ marginTop: 2 }}>
              {(partner.primaryCategory && partner.primaryCategory.name) || titleCase(partner.partnerType)}
            </div>
            <div className="row" style={{ gap: 12, marginTop: 6 }}>
              <Rating value={partner.rating} count={partner.ratingCount} size={13} />
              {isRestaurant ? (
                <div className="row wrap" style={{ gap: 6, marginTop: 8, width: '100%' }}>
                  <span className="pill">
                    {partner.foodType === 'veg' ? '🟢 Pure veg' : partner.foodType === 'non_veg' ? '🔴 Non-veg' : '🟢🔴 Veg & non-veg'}
                  </span>
                  {partner.address && partner.address.city ? (
                    <span className="pill">📍 {[partner.address.line1, partner.address.city].filter(Boolean).join(', ')}</span>
                  ) : null}
                  {minOrderAmount ? <span className="pill">Min order {currency(minOrderAmount)} for delivery</span> : null}
                </div>
              ) : null}
              <Badge
                value={partner.isOpen ? 'Open now' : 'Closed'}
                color={partner.isOpen ? colors.success : colors.danger}
              />
            </div>
          </div>
        </div>

        {partner.description ? (
          <p className="t-small" style={{ marginTop: 12, lineHeight: 1.6 }}>{partner.description}</p>
        ) : null}

        <div className="row wrap" style={{ gap: 14, marginTop: 12 }}>
          {partner.address && partner.address.city ? (
            <span className="t-tiny">
              📍 {[partner.address.line1, partner.address.city].filter(Boolean).join(', ')}
            </span>
          ) : null}
          {partner.openingHours ? (
            <span className="t-tiny">🕘 {partner.openingHours.open} – {partner.openingHours.close}</span>
          ) : null}
          {partner.deliveryAvailable ? (
            <span className="t-tiny">🛵 Delivery ₹{partner.deliveryCharge}</span>
          ) : null}
        </div>

        <div className="row" style={{ gap: 12, marginTop: 16 }}>
          <Button
            title="Call"
            icon="📞"
            variant="ghost"
            full
            onClick={() => { window.location.href = `tel:${partner.phone}`; }}
          />
          <Button
            title="WhatsApp"
            icon="💬"
            variant="success"
            full
            onClick={() => window.open(`https://wa.me/91${whatsappNumber}`, '_blank', 'noopener')}
          />
        </div>

        <div className="row" style={{ gap: 12, marginTop: 10 }}>
          <Button title="Directions" icon="🧭" variant="light" full onClick={() => openDirections(partner)} />
          {(partner.tour || []).length ? (
            <Button
              title="360° showroom"
              icon="🎥"
              variant="light"
              full
              onClick={() => navigate(`/business/${partner._id}/tour`)}
            />
          ) : null}
        </div>

        {(partner.brands || []).length || partner.warrantyInfo || partner.installationAvailable ? (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--line)' }}>
            <div className="t-title" style={{ marginBottom: 8 }}>Brands available</div>
            <div className="row wrap" style={{ gap: 8 }}>
              {(partner.brands || []).map((b) => <span key={b} className="pill">{b}</span>)}
              {partner.installationAvailable ? <span className="pill">🔧 Installation available</span> : null}
              {partner.deliveryAvailable ? <span className="pill">🚚 Home delivery</span> : null}
            </div>
            {partner.warrantyInfo ? (
              <div className="t-small" style={{ marginTop: 10 }}>🛡️ {partner.warrantyInfo}</div>
            ) : null}
          </div>
        ) : null}
      </Reveal>

      {/* Tabs */}
      <div className="row no-bar" style={{ gap: 8, overflowX: 'auto', margin: '18px 0' }}>
        {TABS.map((t) => (
          <Chip key={t.key} label={t.label} active={tab === t.key} onClick={() => setTab(t.key)} />
        ))}
      </div>

      {tab === 'rooms' && data.hotel ? (
        <div className="card sh-sm" style={{ padding: 18 }}>
          <div className="t-title">🛏️ Stay at {data.hotel.name}</div>
          <p className="t-small" style={{ marginTop: 6 }}>See the rooms, pick your dates and book a stay.</p>
          <Button
            title="See rooms & book"
            style={{ marginTop: 12 }}
            onClick={() => navigate(`/hotels/${data.hotel._id}?checkIn=${new Date(Date.now() + 86400000).toISOString().slice(0, 10)}&checkOut=${new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10)}&guests=2`)}
          />
        </div>
      ) : null}

      {tab === 'services' ? (
        <>
          {filterBar}
          {filterModal}
          {services.length ? (
          <div className="grid grid-list">
            {services.map((s, i) => (
              <Reveal key={s._id} delay={stagger(i, 40)}>
                <ServiceCard service={s} onClick={() => navigate(`/service/${s._id}`)} />
              </Reveal>
            ))}
          </div>
          ) : <Empty emoji="🛠️" title="No services listed yet" />}
        </>
      ) : null}

      {tab === 'products' && isRestaurant && !storeMode ? (
        <>
          {filterBar}
          {!products.length ? <Empty emoji="🍽️" title="Nothing on the menu matches" /> : menuSections.map((sec) => (
            <div key={sec} style={{ marginBottom: 18 }}>
              <div className="h3" style={{ margin: '8px 0 10px' }}>{sec}</div>
              <div className="grid grid-list">
                {products.filter((pr) => (pr.menuCategory || 'Other') === sec).map((pr) => (
                  <MenuItemCard
                    key={pr._id}
                    item={pr}
                    inCart={quantityOf(pr._id)}
                    canOrder={Boolean(partner.deliveryAvailable)}
                    onAdd={() => {
                      if ((pr.variants || []).length || (pr.addonGroups || []).length) navigate(`/product/${pr._id}`);
                      else addItem(pr._id, 1).catch(() => {});
                    }}
                    onClick={() => navigate(`/product/${pr._id}`)}
                  />
                ))}
              </div>
            </div>
          ))}

          {filterModal}
        </>
      ) : fashionMode ? (
        <>
          {filterBar}
          {filterModal}
          {/* Garments: the department across the top, its racks under it, the clothes below. */}
          <div className="deptBar">
            {shelves.map((sh) => {
              const on = String(sh._id) === String(openShelf && openShelf._id);
              return (
                <button
                  type="button"
                  key={sh._id}
                  className={`dept ${on ? 'on' : ''}`}
                  onClick={() => { setShelfOpen(sh._id); setRackOpen(null); }}
                >
                  {sh.name}
                </button>
              );
            })}
          </div>

          {racks.length ? (
            <div className="rackRow no-bar">
              {racks.map((r) => {
                const on = String(r._id) === String(openRack && openRack._id);
                return (
                  <button type="button" key={r._id} className={`rack ${on ? 'on' : ''}`} onClick={() => setRackOpen(r._id)}>
                    {r.image
                      ? <img src={fileUrl(r.image)} alt="" />
                      : <span className="rackIcon">{r.icon || '👕'}</span>}
                    <span className="rackName">{r.name}</span>
                  </button>
                );
              })}
            </div>
          ) : null}

          {!rackProducts.length ? <Empty emoji="👕" title="Nothing on this rack yet" /> : (
            <div className="fashionGrid">
              {rackProducts.map((p, i) => (
                <Reveal key={p._id} delay={stagger(i, 30)}>
                  <ProductCard
                    product={p}
                    inCart={quantityOf(p._id)}
                    onAdd={() => addItem(p._id, 1).catch(() => {})}
                    onClick={() => navigate(`/product/${p._id}`)}
                  />
                </Reveal>
              ))}
            </div>
          )}
        </>
      ) : storeMode ? (
        <>
          {filterBar}
          {filterModal}
          {/* The shop laid out like a store: aisles down the side, goods beside them. */}
          <div className="storeView">
            <div className="storeRail">
              {shelves.map((sh) => {
                const on = String(sh._id) === String(openShelf && openShelf._id);
                return (
                  <button type="button" key={sh._id} className={`storeAisle ${on ? 'on' : ''}`} onClick={() => setShelfOpen(sh._id)}>
                    {sh.image
                      ? <img src={fileUrl(sh.image)} alt="" />
                      : <span className="storeAisleIcon">{sh.icon || '🗂️'}</span>}
                    <span className="storeAisleName">{sh.name}</span>
                    <span className="t-tiny">{sh.itemCount}</span>
                  </button>
                );
              })}
            </div>
            <div className="storeShelf">
              <div className="h3" style={{ marginBottom: 10 }}>
                {openShelf ? openShelf.name : 'Everything'}
                <span className="t-tiny" style={{ marginLeft: 8 }}>
                  {shelfProducts.length} item{shelfProducts.length === 1 ? '' : 's'}
                </span>
              </div>
              {!shelfProducts.length ? (
                <Empty emoji={isRestaurant ? '🍽️' : '📦'} title={isRestaurant ? 'Nothing in this section yet' : 'Nothing on this shelf yet'} />
              ) : (
                /* Dish cards run one to a row; a shop's goods sit two to a row. */
                <div className="storeGrid">
                  {shelfProducts.map((p, i) => (
                    <Reveal key={p._id} delay={stagger(i, 35)}>
                      {isRestaurant ? (
                        <MenuItemCard
                          item={p}
                          compact
                          inCart={quantityOf(p._id)}
                          canOrder={Boolean(partner.deliveryAvailable)}
                          onAdd={() => {
                            if ((p.variants || []).length || (p.addonGroups || []).length) navigate(`/product/${p._id}`);
                            else addItem(p._id, 1).catch(() => {});
                          }}
                          onClick={() => navigate(`/product/${p._id}`)}
                        />
                      ) : (
                        <ProductCard
                          product={p}
                          inCart={quantityOf(p._id)}
                          onAdd={() => addItem(p._id, 1).catch(() => {})}
                          onClick={() => navigate(`/product/${p._id}`)}
                        />
                      )}
                    </Reveal>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      ) : tab === 'products' ? (
        <>
          {filterBar}
          {filterModal}
          {products.length ? (
          <div className="grid grid-products">
            {products.map((p, i) => (
              <Reveal key={p._id} delay={stagger(i, 35)}>
                <ProductCard
                  product={p}
                  inCart={quantityOf(p._id)}
                  onAdd={() => addItem(p._id, 1).catch(() => {})}
                  onClick={() => navigate(`/product/${p._id}`)}
                />
              </Reveal>
            ))}
          </div>
          ) : <Empty emoji="📦" title="No products listed yet" />}
        </>
      ) : null}

      {tab === 'menu' ? (
        <>
          <SectionHeader title="Menu" subtitle="Click a page to view it full size" style={{ marginTop: 0 }} />
          <div className="grid grid-cards">
            {menu.map((m, i) => (
              <Reveal key={`${m.image}-${i}`} delay={stagger(i, 50)}>
                <button
                  type="button"
                  onClick={() => setViewer(fileUrl(m.image))}
                  className="card sh-sm press hoverable"
                  style={{ padding: 0, overflow: 'hidden', width: '100%', textAlign: 'left', border: `1px solid ${colors.line}` }}
                >
                  <img src={fileUrl(m.image)} alt={m.title || ''} style={{ width: '100%', height: 210, objectFit: 'cover' }} />
                  {m.title ? <div className="t-small" style={{ fontWeight: 700, color: colors.ink, padding: 12 }}>{m.title}</div> : null}
                </button>
              </Reveal>
            ))}
          </div>
        </>
      ) : null}

      {tab === 'about' ? (
        <Reveal>
          <div className="card sh-sm">
            {[
              ['Owner', partner.ownerName],
              ['Type', titleCase(partner.partnerType)],
              ['Phone', partner.phone],
              ['Address', [
                partner.address && partner.address.line1,
                partner.address && partner.address.landmark,
                partner.address && partner.address.city,
                partner.address && partner.address.pincode,
              ].filter(Boolean).join(', ')],
              ['Working days', ((partner.openingHours && partner.openingHours.days) || []).map(titleCase).join(', ')],
              ['Serves within', partner.serviceRadiusKm ? `${partner.serviceRadiusKm} km` : ''],
              ['Total orders', String(partner.totalOrders || 0)],
              ['Total bookings', String(partner.totalBookings || 0)],
            ].filter(([, v]) => v).map(([label, value]) => (
              <div key={label} className="row" style={{ padding: '7px 0', gap: 12, alignItems: 'flex-start' }}>
                <span className="t-small" style={{ width: 130, flex: '0 0 130px' }}>{label}</span>
                <span className="t-body grow" style={{ fontWeight: 600 }}>{value}</span>
              </div>
            ))}
          </div>

          {partner.video ? (
            <>
              <SectionHeader title="Shop video" />
              <video src={fileUrl(partner.video)} controls style={{ width: '100%', maxHeight: 420, borderRadius: 14, background: '#000', marginBottom: 16 }} />
            </>
          ) : null}

          {gallery.length ? (
            <>
              <SectionHeader title="Shop photos" />
              <div className="rail">
                {gallery.map((url, i) => (
                  <button
                    key={`${url}-${i}`}
                    type="button"
                    onClick={() => setViewer(fileUrl(url))}
                    style={{ border: 0, padding: 0, background: 'none' }}
                  >
                    <img
                      src={fileUrl(url)}
                      alt=""
                      style={{ width: 150, height: 150, objectFit: 'cover', borderRadius: 12 }}
                    />
                  </button>
                ))}
              </div>
            </>
          ) : null}

          {partner.categories && partner.categories.length ? (
            <div className="row wrap" style={{ gap: 8, marginTop: 18 }}>
              {partner.categories.map((c) => (
                <span
                  key={c._id || c}
                  style={{
                    background: colors.primaryLight, padding: '6px 11px',
                    borderRadius: 8, fontSize: 12, color: colors.ink2,
                  }}
                >
                  {c.icon} {c.name || c}
                </span>
              ))}
            </div>
          ) : null}
        </Reveal>
      ) : null}

      {tab === 'reviews' ? (
        !reviews.length ? (
          <Empty emoji="⭐" title="No reviews yet" text="Be the first to review this business after your order." />
        ) : (
          <div className="grid grid-cards">
            {reviews.map((r, i) => (
              <Reveal key={r._id} delay={stagger(i, 50)}>
                <div className="card sh-sm">
                  <div className="row" style={{ gap: 10 }}>
                    <Avatar name={r.user && r.user.name} uri={r.user && r.user.avatar} size={36} />
                    <div className="grow">
                      <div className="t-title">{(r.user && r.user.name) || 'Customer'}</div>
                      <Rating value={r.rating} size={11} />
                    </div>
                  </div>
                  {r.comment ? <p className="t-body" style={{ marginTop: 8, lineHeight: 1.6 }}>{r.comment}</p> : null}
                  {r.reply ? (
                    <div style={{ background: colors.bg, padding: 12, borderRadius: 12, marginTop: 12 }}>
                      <div className="t-tiny">Reply from {partner.businessName}</div>
                      <div className="t-small" style={{ marginTop: 2 }}>{r.reply}</div>
                    </div>
                  ) : null}
                </div>
              </Reveal>
            ))}
          </div>
        )
      ) : null}

      <Modal open={Boolean(viewer)} onClose={() => setViewer(null)} wide>
        <img src={viewer} alt="" style={{ width: '100%', borderRadius: 12 }} />
        <div className="t-small" style={{ textAlign: 'center', marginTop: 12 }}>
          Click outside to close
        </div>
      </Modal>
    </div>
  );
};

export default PartnerDetailPage;
